import React, {useEffect, useState} from 'react';
import {
  getAnonymousRequestToken, getLaunchPayload,
} from '../../../state/guest-thunk';
import {useDispatch} from 'react-redux';
import {AppDispatch} from '../../../../shared/state/store';
import {useParams} from 'react-router';
import {paynowService} from '../../../services/paynow-service';
import {
  PatientEncounter,
} from '../../../../patient/components/models/patient-encounter';
import {
  ApplicationStatusState,
  emptyApplicationStatus, GetError,
} from '../../../../security/model/application-status';
import {logUserOut} from '../../../../security/state/user-thunk';
import {GuestToken} from '../../../../security/services/SSO';
import {EstimateApprovalView} from './estimate-approval-view';
import HttpInterceptor from '../../../../shared/Http/http-interceptor';
import {NoSigAuthDocApprovalPage} from '../no-sig-auth-doc-approval-page';
import {GENERIC_ERROR_MSG} from '../error-messages';
import {
  AuthorizationDocumentStatus,
} from '../payment-program-enrollment/payment-program-enrollment-page';
import {base64ToBlob} from '@finpay-development/shared-platform/lib/esm/shared/utils/pdf-utils';

export const ESTIMATE_AUTH_DOC_TYPE = 'est' as const;

export const EstimateApprovalPage = () => {

  const dispatch = useDispatch<AppDispatch>();
  const params = useParams<{ token?: string }>();

  const [patientEncounter, setPatientEncounter] = useState<PatientEncounter | null>(null);
  const [msg, setMsg] = useState<ApplicationStatusState>(emptyApplicationStatus);
  const [guestToken, setGuestToken] = useState<GuestToken | null>(null);
  const [authDocStatus, setAuthDocStatus] = useState<AuthorizationDocumentStatus | null>(null);
  const [authDocPdfBlob, setAuthDocPdfBlob] = useState<Blob | null>(null);
  const [isAuthDocAlreadyApproved, setIsAuthDocAlreadyApproved] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {

    const getTokenAndEstimateAgreement = async () => {

      /**
       * A temporary measure to log out user session before loading the page.
       * If a patient portal session exists, it interferes with the loading of all guest pages.
       * */
      await dispatch(logUserOut());

      const tokenResponse = await dispatch(getAnonymousRequestToken()).unwrap();

      setGuestToken(tokenResponse);
      HttpInterceptor(false, tokenResponse, true);

      const launchPayloadResponse: any = await dispatch(getLaunchPayload(({
        'encryptedValue': params.token as string,
        'authToken': tokenResponse?.token,
      }))).unwrap();

      if (launchPayloadResponse?.isProcessingError) {
        throw Error('Error decrypting launch payload');
      }

      const patientEncounterResponse = await paynowService.getPatientEncounter(
          +launchPayloadResponse?.patientEncounterId,
          tokenResponse?.token,
          {cacheMode: 'no-cache'},
      );

      if (
          !patientEncounterResponse ||
          patientEncounterResponse.hasErrors ||
          !patientEncounterResponse.patientEncounterId
      ) {
        throw Error('Error fetching patient encounter');
      }

      const authDocStatusId = +launchPayloadResponse?.authDocStatusId;

      const selectedAuthDoc = patientEncounterResponse.authorizationDocumentStatus.find(
          (authDoc: AuthorizationDocumentStatus) => {
            if (authDocStatusId) {
              return authDoc.authorizationDocumentStatusId === authDocStatusId &&
                  authDoc.authorizationDocuments.typeOfDoc === ESTIMATE_AUTH_DOC_TYPE &&
                  authDoc.documentStatus !== 'signed';
            }

            return authDoc.authorizationDocuments.typeOfDoc === ESTIMATE_AUTH_DOC_TYPE &&
                authDoc.documentStatus !== 'signed';
          });

      if (!selectedAuthDoc) {
        setIsAuthDocAlreadyApproved(true);
        return;
      }

      setPatientEncounter(patientEncounterResponse);
      setAuthDocStatus(selectedAuthDoc);

      const estimateId = patientEncounterResponse.pfrEstimateId;
      if (!estimateId) {
        throw Error('Encounter is not tethered to an estimate');
      }

      // Omit isUpload so the PDF is generated for display only (nothing uploaded yet).
      const response = await paynowService.postApi(
          'POST',
          `/estimation/v3/estimate/${estimateId}/estimateAuthDocument`,
          null,
          tokenResponse?.token,
      );

      const contentBase64 = response?.data?.contentBase64;
      if (!contentBase64 || typeof contentBase64 !== 'string') {
        throw Error('Error fetching estimate authorization document');
      }

      setAuthDocPdfBlob(base64ToBlob(contentBase64, response?.data?.contentType));
    };

    getTokenAndEstimateAgreement()
      .catch((err) => {
        console.error(err);
        setHasLoadError(true);
        setMsg(GetError(GENERIC_ERROR_MSG));
      })
      .finally(() => {
        setIsPageLoading(false);
      });

  }, []);

  return (
      <NoSigAuthDocApprovalPage
          isPageLoading={isPageLoading}
          isAuthDocAlreadyApproved={isAuthDocAlreadyApproved}
          areRecordsReady={!!patientEncounter || hasLoadError}
          approvalView={
            <EstimateApprovalView
                patientEncounter={patientEncounter}
                selectedAuthDocStatus={authDocStatus}
                guestToken={guestToken}
                setMessage={(msg: ApplicationStatusState) => setMsg(msg)}
                authDocPdfBlob={authDocPdfBlob}
                loadErrorMsg={hasLoadError ? GENERIC_ERROR_MSG : undefined}
            />
          }
          errorMsg={GENERIC_ERROR_MSG}
          msgState={[msg, setMsg]}
          alreadyApprovedText={'This estimate agreement has already been approved.'}
      />
  );
};
